import {useEffect, useState} from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Avatar from "components/Avatar";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import breakpoints from "assets/theme/base/breakpoints";
import {useRayzayUIController} from "../../context";

// Human-friendly labels for the role pill.
const ROLE_LABELS = {
    SYSTEM_ADMIN: "System Admin",
    COMPANY_ADMIN: "Company Admin",
    BRANCH_ADMIN: "Branch Admin",
    STAFF: "Staff",
    GUARDIAN: "Guardian",
};

function Header() {
    const [tabsOrientation, setTabsOrientation] = useState("horizontal");
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;
    const roleKey = loggedInUserDetail?.userRole;
    const roleLabel = ROLE_LABELS[roleKey] || roleKey;

    useEffect(() => {
        function handleTabsOrientation() {
            return window.innerWidth < breakpoints.values.sm
                ? setTabsOrientation("vertical")
                : setTabsOrientation("horizontal");
        }

        window.addEventListener("resize", handleTabsOrientation);
        handleTabsOrientation();
        return () => window.removeEventListener("resize", handleTabsOrientation);
    }, [tabsOrientation]);

    return (
        <SoftBox position="relative">
            <DashboardNavbar absolute light/>
            <SoftBox
                display="flex"
                alignItems="center"
                position="relative"
                minHeight="9.5rem"
                borderRadius="xl"
                sx={({palette}) => ({
                    // Hero: a single linear dark→light wash in the warning
                    // family. Deeper pastel-yellow top-left lightening
                    // through to soft cream-yellow bottom-right.
                    // Decoupled from `primary` so CTA color swaps don't
                    // change the hero.
                    background: `linear-gradient(135deg, ${palette.warning.focus} 0%, ${palette.warning.main} 50%, ${palette.gradients.warning.state} 100%)`,
                    overflow: "hidden",
                })}
            />
            <Card
                sx={{
                    backdropFilter: `saturate(200%) blur(30px)`,
                    backgroundColor: ({functions: {rgba}, palette: {white}}) => rgba(white.main, 0.85),
                    boxShadow: ({boxShadows: {navbarBoxShadow}}) => navbarBoxShadow,
                    position: "relative",
                    mt: -8,
                    mx: 3,
                    py: 2.5,
                    px: 3,
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item>
                        <Avatar
                            src={`data:image/png;base64,${loggedInUserDetail?.profilePic}`}
                            alt="profile-image"
                            variant="rounded"
                            size="xxl"
                            shadow="md"
                            sx={({palette, borders}) => ({
                                cursor: "pointer",
                                border: `3px solid ${palette.white.main}`,
                                borderRadius: borders.borderRadius.lg,
                                outline: `2px solid ${palette.primary.main}`,
                                outlineOffset: "-1px",
                            })}
                        />
                    </Grid>
                    <Grid item>
                        <SoftBox height="100%" mt={0.5} lineHeight={1.2}>
                            <SoftTypography
                                variant="h4"
                                fontWeight="bold"
                                color="dark"
                                sx={{letterSpacing: "-0.01em"}}
                            >
                                {loggedInUserDetail?.firstName} {loggedInUserDetail?.lastName}
                            </SoftTypography>
                            <SoftBox mt={0.75}>
                                <SoftBox
                                    component="span"
                                    sx={({palette, functions: {rgba}}) => ({
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.5,
                                        borderRadius: "999px",
                                        background: `linear-gradient(135deg, ${rgba(palette.primary.main, 0.18)} 0%, ${rgba(palette.warning.main, 0.18)} 100%)`,
                                        border: `1px solid ${rgba(palette.primary.main, 0.35)}`,
                                    })}
                                >
                                    <SoftBox
                                        sx={({palette}) => ({
                                            width: 6,
                                            height: 6,
                                            borderRadius: "50%",
                                            backgroundColor: palette.primary.main,
                                            boxShadow: `0 0 0 3px ${palette.primary.main}33`,
                                        })}
                                    />
                                    <SoftTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        color="dark"
                                        sx={{letterSpacing: 0.5, textTransform: "uppercase"}}
                                    >
                                        {roleLabel}
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    </Grid>
                </Grid>
            </Card>
        </SoftBox>
    );
}

export default Header;

